import { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Bed, Bath, Maximize, Layers, Building2, Calendar, Car, Sofa,
  CheckCircle2, MapPin, ChevronDown, Home, Thermometer,
  Zap, Trash2, MoreHorizontal, Armchair, ParkingCircle, Wind,
  Grid3X3, ChevronUp
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface PropertyDetailsSectionProps {
  property: any;
  title: string;
  description: string;
  isPlot?: boolean;
  /**
   * When true, renders sections stacked with anchor IDs (no Tabs shell).
   * Used by the mobile layout where a sticky scroll-spy strip is rendered separately.
   */
  mobileLayout?: boolean;
}

export function PropertyDetailsSection({ property, title, description, isPlot = false, mobileLayout = false }: PropertyDetailsSectionProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);

  const formatPrice = (price: number, currency: string) =>
    currency === 'USD' ? `$${price.toLocaleString()}` : `ETB ${price.toLocaleString()}`;

  const descriptionTooLong = description && description.length > 400;
  const displayDescription = showFullDesc ? description : description?.slice(0, 400);

  // Parse features
  const features = property.features || {};
  const featureCategories = typeof features === 'object' && !Array.isArray(features) ? features : {};

  // Details table rows - different for plots vs regular properties
  const detailsTable = isPlot ? [
    { label: 'Type', value: 'Residential Plot', label2: 'Purpose', value2: property.listing_type === 'sale' ? 'For Sale' : 'For Rent' },
    { label: 'Price', value: formatPrice(property.price, property.currency || 'ETB'), label2: 'Price per m²', value2: property.area_sqm ? formatPrice(Math.round(property.price / property.area_sqm), property.currency || 'ETB') : '-' },
    { label: 'Area', value: property.area_sqm ? `${property.area_sqm} m²` : '-', label2: 'Area (sqft)', value2: property.area_sqm ? `${(property.area_sqm * 10.764).toFixed(0)} sqft` : '-' },
    { label: 'Location', value: `${property.city}${property.sub_city ? ', ' + property.sub_city : ''}`, label2: 'Added', value2: property.created_at ? new Date(property.created_at).toLocaleDateString() : '-' },
  ] : [
    { label: 'Type', value: property.property_type, label2: 'Purpose', value2: property.listing_type === 'sale' ? 'For Sale' : 'For Rent' },
    { label: 'Price', value: formatPrice(property.price, property.currency || 'ETB'), label2: 'Bedroom(s)', value2: property.bedrooms },
    { label: 'Bath(s)', value: property.bathrooms, label2: 'Added', value2: property.created_at ? new Date(property.created_at).toLocaleDateString() : '-' },
    { label: 'Area', value: property.area_sqm ? `${property.area_sqm} m²` : '-', label2: 'Location', value2: `${property.city}${property.sub_city ? ', ' + property.sub_city : ''}` },
  ];

  // Main features for amenities display (skip for plots)
  const mainFeatures = isPlot ? [] : [
    property.year_built && { icon: Calendar, label: `Built in year: ${property.year_built}` },
    property.has_parking && { icon: ParkingCircle, label: 'Parking Spaces' },
    property.is_furnished && { icon: Armchair, label: 'Furnished' },
    property.total_floors && { icon: Building2, label: `Floors: ${property.total_floors}` },
    property.floor && { icon: Layers, label: `Floor: ${property.floor}` },
    property.bedrooms && { icon: Bed, label: `Bedrooms: ${property.bedrooms}` },
    property.bathrooms && { icon: Bath, label: `Bathrooms: ${property.bathrooms}` },
    property.area_sqm && { icon: Maximize, label: `Area: ${property.area_sqm} m²` },
  ].filter(Boolean) as { icon: any; label: string }[];

  // Plot-specific features
  const plotFeatures = isPlot ? [
    property.area_sqm && { icon: Maximize, label: `Plot Size: ${property.area_sqm} m²` },
    property.area_sqm && { icon: Grid3X3, label: `${(property.area_sqm * 10.764).toFixed(0)} sqft` },
    property.address && { icon: MapPin, label: property.address },
    { icon: Layers, label: property.listing_type === 'sale' ? 'For Sale' : 'For Rent' },
  ].filter(Boolean) as { icon: any; label: string }[] : [];

  // ===== Reusable section content blocks =====

  const overviewContent = (
    <div className="space-y-6 pt-6">
      <h2 className="text-xl font-bold">Overview</h2>

      {/* Details Table */}
      <div>
        <h3 className="font-semibold text-base mb-3">Details</h3>
        <div className="border rounded-lg overflow-hidden">
          {detailsTable.map((row, i) => (
            <div key={i} className={`grid grid-cols-1 sm:grid-cols-2 ${i < detailsTable.length - 1 ? 'border-b' : ''}`}>
              <div className="grid grid-cols-[100px_1fr] sm:border-r border-b sm:border-b-0">
                <span className="text-sm text-muted-foreground py-2.5 px-3 bg-muted/30 font-medium">{row.label}</span>
                <span className="text-sm font-semibold py-2.5 px-3 capitalize break-words min-w-0">{row.value || '-'}</span>
              </div>
              <div className="grid grid-cols-[100px_1fr]">
                <span className="text-sm text-muted-foreground py-2.5 px-3 bg-muted/30 font-medium">{row.label2}</span>
                <span className="text-sm font-semibold py-2.5 px-3 break-words min-w-0">{row.value2 || '-'}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* Description */}
      {description && (
        <div>
          <h3 className="font-semibold text-base mb-3">Description</h3>
          <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
            {displayDescription}
            {descriptionTooLong && !showFullDesc && '...'}
          </p>
          {descriptionTooLong && (
            <button
              onClick={() => setShowFullDesc(!showFullDesc)}
              className="text-primary text-sm font-semibold mt-2 hover:underline flex items-center gap-1"
            >
              {showFullDesc ? 'Show Less' : 'Read More'}
              {showFullDesc ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
        </div>
      )}

      <Separator />

      {/* Amenities / Plot Features Section */}
      {isPlot ? (
        <div>
          <h3 className="font-semibold text-base mb-4">Plot Details</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {plotFeatures.map((f, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border">
                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <f.icon className="h-4 w-4 text-primary" />
                </div>
                <span className="text-sm font-medium">{f.label}</span>
              </div>
            ))}
          </div>

          {Object.keys(featureCategories).length > 0 && (
            <div className="mt-6 space-y-4">
              {Object.entries(featureCategories).map(([category, items]) => (
                <div key={category} className="flex items-start gap-4">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-1 w-24 shrink-0 capitalize">{category}</span>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 flex-1">
                    {(Array.isArray(items) ? items : []).map((item: string, i: number) => (
                      <div key={i} className="flex items-center gap-2.5 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          <h3 className="font-semibold text-base mb-4">Amenities</h3>

          {mainFeatures.length > 0 && (
            <div className="mb-4">
              <div className="flex items-start gap-4">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-2 w-24 shrink-0">Main Features</span>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 flex-1">
                  {(showAllAmenities ? mainFeatures : mainFeatures.slice(0, 9)).map((f, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-sm">
                      <f.icon className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="underline decoration-dotted underline-offset-4 cursor-default">{f.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {property.property_amenities && property.property_amenities.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                {property.property_amenities.map((pa: any) => {
                  const name = typeof pa.amenities?.name === 'object' ? (pa.amenities.name[lang] || pa.amenities.name.en) : pa.amenities?.name;
                  return (
                    <div key={pa.amenity_id} className="flex items-center gap-2.5 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                      <span>{name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {Object.keys(featureCategories).length > 0 && (
            <div className="mt-4 pt-4 border-t space-y-4">
              {Object.entries(featureCategories).map(([category, items]) => (
                <div key={category} className="flex items-start gap-4">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-1 w-24 shrink-0 capitalize">{category}</span>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 flex-1">
                    {(Array.isArray(items) ? items : []).map((item: string, i: number) => (
                      <div key={i} className="flex items-center gap-2.5 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {mainFeatures.length > 9 && (
            <button
              onClick={() => setShowAllAmenities(!showAllAmenities)}
              className="text-primary text-sm font-semibold mt-3 hover:underline flex items-center gap-1"
            >
              {showAllAmenities ? 'Show Less' : 'View More'}
              {showAllAmenities ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
        </div>
      )}
    </div>
  );

  const locationContent = (
    <div className="space-y-6 pt-6">
      <h2 className="text-xl font-bold">Location & Nearby</h2>

      {/* Map */}
      <div className="rounded-xl overflow-hidden border bg-muted relative">
        {property.latitude && property.longitude ? (
          <iframe
            title="Property Location"
            width="100%"
            height="400"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${property.latitude},${property.longitude}&zoom=15`}
          />
        ) : (
          <div className="h-[300px] sm:h-[400px] flex flex-col items-center justify-center text-muted-foreground p-4 text-center">
            <MapPin className="h-10 w-10 mb-3 text-primary" />
            <p className="font-semibold text-lg">
              {property.city}{property.sub_city ? ` - ${property.sub_city}` : ''}
            </p>
            <p className="text-sm mt-1">{property.address || 'Exact location not available on map'}</p>
            <p className="text-xs mt-4 max-w-md">
              Find out the location of the property on an interactive map
            </p>
          </div>
        )}
      </div>

      {/* Location Details */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <h4 className="font-semibold text-sm mb-2">Address Details</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex justify-between gap-2"><span>City</span><span className="font-medium text-foreground text-right">{property.city}</span></div>
              {property.sub_city && <div className="flex justify-between gap-2"><span>Sub City</span><span className="font-medium text-foreground text-right">{property.sub_city}</span></div>}
              {property.woreda && <div className="flex justify-between gap-2"><span>Woreda</span><span className="font-medium text-foreground text-right">{property.woreda}</span></div>}
              {property.address && <div className="flex justify-between gap-2"><span>Address</span><span className="font-medium text-foreground text-right break-words">{property.address}</span></div>}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <h4 className="font-semibold text-sm mb-2">Coordinates</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex justify-between gap-2"><span>Latitude</span><span className="font-medium text-foreground">{property.latitude || 'N/A'}</span></div>
              <div className="flex justify-between gap-2"><span>Longitude</span><span className="font-medium text-foreground">{property.longitude || 'N/A'}</span></div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const homeFinanceContent = (
    <div className="space-y-6 pt-6">
      <h2 className="text-xl font-bold">Home Finance</h2>
      <p className="text-muted-foreground text-sm">Calculate and view the monthly mortgage on this property</p>
      <PropertyMortgageCalculator price={property.price} currency={property.currency || 'ETB'} />
    </div>
  );

  const priceIndexContent = (
    <div className="pt-6">
      <PropertyPriceIndex property={property} />
    </div>
  );

  // ===== Mobile layout: stacked anchored sections (no Tabs shell) =====
  if (mobileLayout) {
    return (
      <div className="space-y-8">
        <section id="overview" className="scroll-mt-[120px]">{overviewContent}</section>
        <Separator />
        <section id="nearby" className="scroll-mt-[120px]">{locationContent}</section>
        <Separator />
        <section id="home-finance" className="scroll-mt-[120px]">{homeFinanceContent}</section>
        <Separator />
        <section id="price-index" className="scroll-mt-[120px]">{priceIndexContent}</section>
      </div>
    );
  }

  // ===== Desktop layout: original sticky tab strip =====
  return (
    <div className="space-y-0">
      <Tabs defaultValue="overview" className="w-full">
        <div className="sticky top-[64px] z-30 bg-background border-b">
          <div className="overflow-x-auto no-scrollbar relative after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-8 after:bg-gradient-to-l after:from-background after:to-transparent">
            <TabsList className="inline-flex w-max justify-start rounded-none bg-transparent h-auto p-0 gap-0">
              {['overview', 'location', 'home-finance'].map(tab => (
                <TabsTrigger
                  key={tab}
                  value={tab}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 sm:px-5 py-3 text-sm font-medium capitalize data-[state=active]:text-primary whitespace-nowrap"
                >
                  {tab === 'overview' ? 'Overview' : tab === 'location' ? 'Location & Nearby' : 'Home Finance'}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </div>

        <TabsContent value="overview" className="mt-0">
          {overviewContent}
          <Separator className="my-6" />
          {priceIndexContent}
        </TabsContent>

        <TabsContent value="location" className="mt-0">
          {locationContent}
        </TabsContent>

        <TabsContent value="home-finance" className="mt-0">
          {homeFinanceContent}
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ===== Price Index Section ===== */
function PropertyPriceIndex({ property }: { property: any }) {
  const [timeRange, setTimeRange] = useState<'6m' | '1y' | 'max'>('max');
  const currency = property.currency || 'ETB';

  // Generate simulated price trend data based on property price
  const allData = useMemo(() => {
    const basePrice = property.price;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const data = [];
    const now = new Date();
    for (let i = 24; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const drift = 1 + (24 - i) * 0.012 + Math.sin(i * 0.5) * 0.03;
      data.push({
        month: `${months[d.getMonth()]} ${d.getFullYear().toString().slice(2)}`,
        price: Math.round(basePrice * drift * (0.85 + Math.random() * 0.05)),
        idx: i,
      });
    }
    return data;
  }, [property.price]);

  const chartData = timeRange === '6m' ? allData.slice(-7) : timeRange === '1y' ? allData.slice(-13) : allData;

  const currentPrice = chartData[chartData.length - 1]?.price || property.price;
  const oldestPrice = chartData[0]?.price || property.price;
  const priceChange = currentPrice - oldestPrice;
  const priceChangePct = oldestPrice > 0 ? ((priceChange / oldestPrice) * 100).toFixed(1) : '0';

  const formatCurrency = (val: number) =>
    currency === 'USD' ? `$${val.toLocaleString()}` : `ETB ${val.toLocaleString()}`;

  // Simulated trend locations
  const trendLocations = [
    { rank: 1, name: property.sub_city || property.city, pct: 78.5, change: 'No Change' },
    { rank: 2, name: 'Bole', pct: 12.3, change: '+1' },
    { rank: 3, name: 'Kazanchis', pct: 4.8, change: '-1' },
    { rank: 4, name: 'CMC', pct: 2.1, change: 'No Change' },
    { rank: 5, name: 'Megenagna', pct: 1.5, change: '+2' },
    { rank: 6, name: 'Sarbet', pct: 0.8, change: '-1' },
  ];

  return (
    <div className="space-y-6">
      <h3 className="font-semibold text-base">Price Index</h3>

      {/* Title */}
      <div>
        <h4 className="text-lg font-bold text-primary">
          {property.city}{property.sub_city ? ` - ${property.sub_city}` : ''} {property.property_type}s
        </h4>
        <div className="h-1 w-16 bg-primary rounded mt-1" />
      </div>

      {/* Price Index Chart + Stats */}
      <div className="grid md:grid-cols-[280px_1fr] gap-6">
        {/* Left stats */}
        <div className="space-y-4">
          <div>
            <p className="text-xs text-muted-foreground">Current Price (Estimated)</p>
            <p className="text-2xl font-bold">{formatCurrency(currentPrice)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Price change ({chartData[0]?.month} - {chartData[chartData.length - 1]?.month})</p>
            <p className={`text-lg font-bold ${priceChange >= 0 ? 'text-green-600' : 'text-destructive'}`}>
              {priceChange >= 0 ? '+' : ''}{formatCurrency(priceChange)} ({priceChangePct}%)
            </p>
          </div>
          <Separator />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">6 months ago</span><span className="font-semibold">{formatCurrency(allData[allData.length - 7]?.price || property.price)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">12 months ago</span><span className="font-semibold">{formatCurrency(allData[allData.length - 13]?.price || property.price)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">24 months ago</span><span className="font-semibold">{formatCurrency(allData[0]?.price || property.price)}</span></div>
          </div>
        </div>

        {/* Right chart */}
        <div>
          {/* Time range buttons */}
          <div className="flex justify-center mb-4">
            <div className="inline-flex border rounded-lg overflow-hidden">
              {(['6m', '1y', 'max'] as const).map(range => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className={`px-5 py-2 text-sm font-medium transition-colors ${timeRange === range ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted text-muted-foreground'}`}
                >
                  {range === '6m' ? '6 Months' : range === '1y' ? '1 Year' : 'Max'}
                </button>
              ))}
            </div>
          </div>

          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis
                  tickFormatter={(v) => currency === 'USD' ? `$${(v / 1000).toFixed(0)}K` : `${(v / 1000000).toFixed(1)}M`}
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                  width={65}
                />
                <Tooltip
                  formatter={(value: number) => [formatCurrency(value), 'Price']}
                  contentStyle={{ borderRadius: '8px', fontSize: '12px', border: '1px solid hsl(var(--border))' }}
                />
                <Line type="monotone" dataKey="price" stroke="hsl(var(--destructive))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <Separator />

      {/* Trends - Most Searched Locations */}
      <div>
        <h3 className="font-semibold text-base mb-4">Trends - Most Searched Locations in {property.city}</h3>
        <div className="border rounded-lg overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-[60px_1fr_1fr_120px] bg-muted/50 border-b px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            <span>Rank</span>
            <span>Locality</span>
            <span>Percentage of Total Searches (%)</span>
            <span className="text-right">Performance</span>
          </div>
          {/* Rows */}
          {trendLocations.map(loc => (
            <div key={loc.rank} className="grid grid-cols-[60px_1fr_1fr_120px] px-4 py-3 border-b last:border-b-0 items-center text-sm hover:bg-muted/30 transition-colors">
              <span className="text-muted-foreground font-medium">{loc.rank}</span>
              <span className="font-medium">{loc.name}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-8">{loc.pct}</span>
                <div className="flex-1 bg-muted rounded-full h-4 max-w-[200px]">
                  <div className="bg-primary h-4 rounded-full transition-all" style={{ width: `${Math.min(loc.pct, 100)}%` }} />
                </div>
              </div>
              <span className={`text-right text-xs font-semibold ${loc.change.startsWith('+') ? 'text-green-600' : loc.change.startsWith('-') ? 'text-destructive' : 'text-muted-foreground'}`}>
                {loc.change}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ===== Inline Mortgage Calculator ===== */
function PropertyMortgageCalculator({ price, currency }: { price: number; currency: string }) {
  const [loanType, setLoanType] = useState('standard');
  const [propertyPrice, setPropertyPrice] = useState(price);
  const [loanPeriod, setLoanPeriod] = useState(20);
  const [downPaymentPct, setDownPaymentPct] = useState(20);
  const [interestRate, setInterestRate] = useState(14.5);

  const loanTypes = [
    { id: 'standard', name: 'Standard', rate: 14.5 },
    { id: 'diaspora', name: 'Diaspora', rate: 12.0 },
    { id: 'affordable', name: 'Affordable Housing', rate: 8.0 },
    { id: 'construction', name: 'Construction', rate: 16.0 },
  ];

  const handleLoanTypeChange = (val: string) => {
    setLoanType(val);
    const found = loanTypes.find(l => l.id === val);
    if (found) setInterestRate(found.rate);
  };

  const downPayment = (propertyPrice * downPaymentPct) / 100;
  const loanAmount = propertyPrice - downPayment;
  const monthlyRate = interestRate / 100 / 12;
  const numPayments = loanPeriod * 12;
  const monthlyPayment = monthlyRate > 0
    ? (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1)
    : loanAmount / numPayments;
  const totalPayment = monthlyPayment * numPayments;
  const totalInterest = totalPayment - loanAmount;
  const interestPct = totalPayment > 0 ? (totalInterest / totalPayment) * 100 : 0;

  const formatCurrency = (val: number) =>
    currency === 'USD' ? `$${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : `ETB ${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div className="grid md:grid-cols-[1fr_320px] gap-6">
      {/* Left: Controls */}
      <Card>
        <CardContent className="p-6 space-y-5">
          {/* Loan Type */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-primary">Loan Type</Label>
            <Select value={loanType} onValueChange={handleLoanTypeChange}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {loanTypes.map(lt => (
                  <SelectItem key={lt.id} value={lt.id}>{lt.name} ({lt.rate}%)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Property Price */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Property Price</Label>
            <Slider
              value={[propertyPrice]}
              onValueChange={([v]) => setPropertyPrice(v)}
              min={100000}
              max={100000000}
              step={50000}
              className="mt-2"
            />
            <div className="text-right text-sm font-semibold">{formatCurrency(propertyPrice)}</div>
          </div>

          {/* Loan Period */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Loan Period</Label>
            <Slider
              value={[loanPeriod]}
              onValueChange={([v]) => setLoanPeriod(v)}
              min={1}
              max={30}
              step={1}
              className="mt-2"
            />
            <div className="text-right text-sm font-semibold">{loanPeriod} Years</div>
          </div>

          {/* Down Payment */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Down Payment</Label>
            <Slider
              value={[downPaymentPct]}
              onValueChange={([v]) => setDownPaymentPct(v)}
              min={0}
              max={90}
              step={5}
              className="mt-2"
            />
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{downPaymentPct}%</span>
              <span className="font-semibold">{formatCurrency(downPayment)}</span>
            </div>
          </div>

          {/* Interest Rate */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Interest Rate</Label>
            <Slider
              value={[interestRate]}
              onValueChange={([v]) => setInterestRate(v)}
              min={1}
              max={25}
              step={0.5}
              className="mt-2"
            />
            <div className="text-right text-sm font-semibold">{interestRate}%</div>
          </div>
        </CardContent>
      </Card>

      {/* Right: Results */}
      <div className="space-y-4">
        <Card className="border-primary/20">
          <CardContent className="p-6 text-center space-y-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Monthly Payment</p>
            <p className="text-xs text-muted-foreground">{currency}</p>
            <p className="text-3xl font-bold text-foreground">{formatCurrency(monthlyPayment)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center space-y-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Bank Finance Amount</p>
            <p className="text-xs text-muted-foreground">{currency}</p>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(loanAmount)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider text-center">Payment Breakdown</p>
            {/* Bar visualization */}
            <div className="flex h-5 rounded-full overflow-hidden">
              <div className="bg-primary transition-all" style={{ width: `${interestPct}%` }} />
              <div className="bg-accent transition-all" style={{ width: `${100 - interestPct}%` }} />
            </div>
            <div className="flex justify-between text-xs">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm bg-primary inline-block" />Interest
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm bg-accent inline-block" />Principal
              </span>
            </div>
          </CardContent>
        </Card>

        <Link to="/mortgage-calculator">
          <Button className="w-full font-semibold" variant="default">
            Full Mortgage Calculator
          </Button>
        </Link>
      </div>
    </div>
  );
}
